import { useEffect, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";

/**
 * Rotating hero media carousel — videos autoplay/loop/muted, image stays
 * on screen for a fixed dwell time. Fades cross between slides for a
 * premium feel. Falls back to poster + a "tap to play" affordance if the
 * browser blocks autoplay.
 *
 * Prop shape:
 *   items: [
 *     { type: "video", src: "/videos/hero/hero-1.mp4", poster: "…", alt: "…" },
 *     { type: "image", src: "/img/hero/hero-4.webp", alt: "…" },
 *   ]
 *   imageDwellMs?: number  // how long an image slide stays on screen (default 5000)
 *   className?: string     // wrapper className (aspect ratio + rounding here)
 */
export function HeroMediaCarousel({ items = [], imageDwellMs = 5000, className = "" }) {
  const [i, setI] = useState(0);
  const timerRef = useRef(null);
  const videoRefs = useRef(new Map());

  const clearTimer = () => { if (timerRef.current) { clearTimeout(timerRef.current); timerRef.current = null; } };
  const advance = () => setI((n) => (n + 1) % items.length);

  useEffect(() => {
    clearTimer();
    const cur = items[i];
    if (!cur) return;
    if (cur.type === "image") {
      timerRef.current = setTimeout(advance, imageDwellMs);
    } else if (cur.type === "video") {
      // Video advance is driven by `onEnded` (loop=false so onEnded fires).
      // But we also set a max ceiling in case the file is very long — cap 12s.
      timerRef.current = setTimeout(advance, cur.maxMs || 12000);
    }
    return clearTimer;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [i, items]);

  // Try to (re)start the video on slide change. Some browsers pause bg videos.
  useEffect(() => {
    const cur = items[i];
    if (cur?.type !== "video") return;
    const el = videoRefs.current.get(i);
    if (el) {
      // Kick the buffer — some browsers won't start until load() is called
      // explicitly when the element is remounted inside AnimatePresence.
      try { el.load(); el.play().catch(() => {}); } catch {}
    }
  }, [i, items]);

  if (!items.length) return null;

  return (
    <div className={`relative overflow-hidden bg-black ${className}`} data-testid="hero-media-carousel">
      <AnimatePresence initial={false} mode="sync">
        <motion.div
          key={i}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.9, ease: [0.4, 0, 0.2, 1] }}
          className="absolute inset-0"
          data-testid={`hero-media-slide-${i}`}
        >
          {items[i].type === "video" ? (
            <video
              ref={(el) => {
                if (el) {
                  videoRefs.current.set(i, el);
                  // Kick playback as soon as the element is attached.
                  try { el.play().catch(() => {}); } catch {}
                }
              }}
              src={items[i].src}
              poster={items[i].poster}
              autoPlay
              muted
              playsInline
              // loop off — we advance to the next slide on `ended` for variety
              onEnded={advance}
              onCanPlay={(e) => { try { e.currentTarget.play().catch(() => {}); } catch {} }}
              preload="auto"
              aria-label={items[i].alt || "Hero video"}
              className="h-full w-full object-cover"
            />
          ) : (
            <img
              src={items[i].src}
              alt={items[i].alt || "Hero photo"}
              loading="eager"
              className="h-full w-full object-cover"
            />
          )}
        </motion.div>
      </AnimatePresence>

      {/* Slide dots + live pill */}
      <div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex items-center gap-1.5 z-10">
        {items.map((_, idx) => (
          <button
            key={idx}
            onClick={() => setI(idx)}
            aria-label={`Show slide ${idx + 1}`}
            data-testid={`hero-media-dot-${idx}`}
            className={`h-1.5 rounded-full transition-all ${idx === i ? "w-6 bg-white" : "w-1.5 bg-white/40 hover:bg-white/70"}`}
          />
        ))}
      </div>
      <div className="absolute top-3 left-3 z-10 inline-flex items-center gap-1.5 rounded-full bg-black/50 backdrop-blur px-2 py-1 text-[10px] uppercase tracking-[0.22em] text-white/80 border border-white/15">
        <span className="h-1.5 w-1.5 rounded-full bg-accent animate-pulse" /> Live
      </div>
    </div>
  );
}
